import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  CircularProgress
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Badge as BadgeIcon
} from "@mui/icons-material";
import { colors, fonts, spacing, borderRadius, shadows } from "../../constants";
import { PRIMARY_BUTTON_STYLES } from "../../constants/settingsPage";
import { getUserInitials } from "../../utils/settingsUtils";
import SectionHeader from "./SectionHeader";
import ProfileField from "./ProfileField";

/**
 * ProfileSection Component
 * Profile Information card component
 */
const ProfileSection = ({
  user,
  profileData,
  isEditingProfile,
  isSavingProfile,
  onProfileChange,
  onEditToggle,
  onSave,
  onCancel,
  roleDisplayName
}) => {
  const userData = JSON.parse(localStorage.getItem("userData"));
  // Handle phone number change - strip prefixes, spaces, and limit to 10 digits
  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    // Remove spaces, dashes, parentheses, dots
    let cleaned = inputValue.replace(/[\s\-\(\)\.]/g, "");
    // Strip +91, 91, or leading 0 prefix
    if (cleaned.startsWith("+91")) cleaned = cleaned.substring(3);
    else if (cleaned.startsWith("91") && cleaned.replace(/\D/g, "").length > 10)
      cleaned = cleaned.substring(2);
    else if (cleaned.startsWith("0")) cleaned = cleaned.substring(1);
    // Remove remaining non-numeric characters
    const numericValue = cleaned.replace(/\D/g, "");
    // Limit to 10 digits
    const limitedValue = numericValue.slice(0, 10);
    onProfileChange("phone", limitedValue);
  };

  // Validate phone number - must be exactly 10 digits
  const phoneError =
    profileData.phone &&
    profileData.phone.length > 0 &&
    profileData.phone.length !== 10;
  const phoneErrorText = phoneError
    ? "Phone number must be exactly 10 digits"
    : null;

  return (
    <Card
      sx={{
        mb: spacing.sm,
        boxShadow: shadows.md,
        borderRadius: borderRadius.card,
        border: `1px solid ${colors.borderDefault}`,
        backgroundColor: colors.backgroundWhite,
        overflow: "visible",
        width: "100%",
        maxWidth: "100%"
      }}
    >
      <CardContent sx={{ p: spacing.sm }}>
        <SectionHeader
          icon={PersonIcon}
          title="Profile Information"
          // description="Update your personal information and contact details"
          actionButtons={
            !isEditingProfile ? (
              // <Button
              //   startIcon={<EditIcon />}
              //   onClick={onEditToggle}
              //   size="small"
              //   variant="outlined"
              //   sx={{
              //     borderColor: colors.primaryDark,
              //     color: colors.primaryDark,
              //     textTransform: "none",
              //     fontWeight: fonts.weightMedium,
              //     px: spacing.md,
              //     py: spacing.xs,
              //     borderRadius: borderRadius.md,
              //     fontSize: fonts.fontSizeSm,
              //     "&:hover": {
              //       borderColor: colors.primary,
              //       backgroundColor: colors.primarySoft,
              //       color: colors.primary
              //     }
              //   }}
              // >
              //   Edit Profile
              // </Button>
              <></>
            ) : (
              <Box sx={{ display: "flex", gap: spacing.xs }}>
                <Button
                  onClick={onCancel}
                  size="small"
                  variant="outlined"
                  disabled={isSavingProfile}
                  sx={{
                    borderColor: colors.borderHover,
                    color: colors.textSecondary,
                    textTransform: "none",
                    fontWeight: fonts.weightMedium,
                    px: spacing.md,
                    py: spacing.xs,
                    borderRadius: borderRadius.md,
                    fontSize: fonts.fontSizeSm,
                    "&:hover": {
                      borderColor: colors.borderHover,
                      backgroundColor: colors.backgroundSoft
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  startIcon={
                    isSavingProfile ? (
                      <CircularProgress
                        size={16}
                        sx={{ color: colors.textWhite }}
                      />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={onSave}
                  variant="contained"
                  size="small"
                  disabled={isSavingProfile}
                  sx={{
                    ...PRIMARY_BUTTON_STYLES,
                    fontWeight: fonts.weightMedium,
                    fontSize: fonts.fontSizeSm,
                    px: spacing.md,
                    py: spacing.xs,
                    borderRadius: borderRadius.md
                  }}
                >
                  {isSavingProfile ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            )
          }
        />

        <Box
          sx={{
            mb: spacing.sm,
            p: spacing.sm,
            backgroundColor: colors.backgroundSoft,
            borderRadius: borderRadius.md,
            border: `1px solid ${colors.borderDefault}`
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: spacing.sm
            }}
          >
            <Avatar
              sx={{
                width: 60,
                height: 60,
                background: colors.avatarGradient,
                fontSize: fonts.fontSizeXl,
                fontWeight: fonts.weightBold,
                color: colors.avatarText,
                boxShadow: `0 4px 15px ${colors.avatarShadow}`,
                border: `2px solid ${colors.backgroundWhite}`
              }}
            >
              {getUserInitials(profileData.name)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: fonts.weightSemiBold,
                  fontSize: fonts.fontSizeMd,
                  color: colors.textPrimaryDark,
                  mb: spacing.xs
                }}
              >
                {profileData.name || "User Name"}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: spacing.xs,
                  px: spacing.sm,
                  py: spacing.xs,
                  backgroundColor: colors.backgroundWhite,
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.borderDefault}`
                }}
              >
                <BadgeIcon
                  sx={{ color: colors.primary, fontSize: fonts.fontSizeSm }}
                />
                <Typography
                  sx={{
                    color: colors.textPrimaryDark,
                    fontSize: fonts.fontSizeSm,
                    fontWeight: fonts.weightSemiBold,
                    textTransform: "none"
                  }}
                >
                  {userData?.designm || profileData?.designation}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: spacing.sm }}>
          <Typography
            component="div"
            sx={{
              fontSize: fonts.fontSizeMd,
              color: colors.textPrimaryDark,
              fontWeight: fonts.weightSemiBold,
              mb: spacing.sm,
              display: "flex",
              alignItems: "center",
              gap: spacing.xs
            }}
          >
            <Box
              sx={{
                width: 3,
                height: 16,
                background: colors.primaryGradient,
                borderRadius: borderRadius.full
              }}
            />
            Personal Details
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: spacing.md,
            width: "100%",
            "& > *": {
              minWidth: 0
            }
          }}
        >
          <ProfileField
            label="Full Name"
            icon={PersonIcon}
            value={profileData.name}
            onChange={(e) => onProfileChange("name", e.target.value)}
            disabled={!isEditingProfile}
            placeholder="Enter your full name"
            type="text"
          />
          <ProfileField
            label="Email"
            icon={EmailIcon}
            value={profileData.email}
            disabled={true}
            placeholder="Enter your email address"
            type="email"
          />
          <ProfileField
            label="Phone Number"
            icon={PhoneIcon}
            value={profileData.phone}
            onChange={handlePhoneChange}
            disabled={!isEditingProfile}
            placeholder="Enter your phone number"
            type="tel"
            error={phoneError}
            errorText={phoneErrorText}
            helperText={
              !phoneError && isEditingProfile ? "Enter 10 digits" : null
            }
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfileSection;
